import styles from "./ReferAndEarnPage.module.css";
import { useMemo, useState } from "react";
import { useReferralSettings } from "@/features/referrals/viewmodel";
import QRCode from "react-qr-code";
import { ChevronDownIcon } from "@/assets/svg";
import { Modal } from "@/shared/modal";

const referralFaqs = [
  {
    question: "Why is there an annual fee for protesting?",
    answer:
      "Property taxes are reassessed annually, and each year presents new opportunities to challenge your assessment. The annual fee covers ongoing monitoring, analysis, and filing support each tax year.",
  },
  {
    question: "Do I have to protest every year?",
    answer:
      "You do not have to protest every year, but assessments can change annually. Reviewing your value each year helps ensure you are not overpaying.",
  },
  {
    question: "What if there is no value change?",
    answer:
      "If there is no meaningful value change, a protest may not be necessary that year. We still monitor future assessments for new savings opportunities.",
  },
  {
    question: "Can my property tax go up every year?",
    answer:
      "Tax rates can change year to year, but protesting helps keep your assessed value fair and can reduce over-assessment risk.",
  },
];

export const ReferAndEarnPage = () => {
  const {
    referralShareUrl,
    referralCode,
    referralCount,
    referralStats,
    statistics,
    transactions,
    referralsList,
    statusFilter,
    copied,
    setStatusFilter,
    copyReferralCode,
    shareReferralCode,
    openEmail,
    openSMS,
    isLoadingTransactions,
    isLoadingReferrals,
  } = useReferralSettings();
  const [activeTab, setActiveTab] = useState<"activity" | "myRewards">("activity");
  const [isQrOpen, setIsQrOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const qrValue = referralShareUrl || referralCode || "";
  const toggleFaq = (index: number) => {
    setOpenFaqIndex((current) => (current === index ? null : index));
  };

  const activityRows = useMemo(
    () =>
      (activeTab === "myRewards" ? referralsList : transactions).map((item) => {
        const amount = Number(item.rewards_points ?? item.rewardPointsAwarded ?? 0);
        const source =
          item.title ||
          item.message ||
          item.refered_user?.email ||
          item.referal_user?.email ||
          item.name ||
          "Referral";
        const dateValue = item.created_at || item.completed_at || item.signup_at || "";

        return {
          source,
          date: dateValue ? new Date(dateValue).toLocaleDateString() : "-",
          amount: `${amount >= 0 ? "+" : "-"}$${Math.abs(amount).toFixed(2)}`,
          status: item.status || "pending",
          referal_user: item?.referal_user?.first_name + " (" + item?.referal_user?.email + ")",
        };
      }),
    [activeTab, referralsList, transactions]
  );

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroLeft}>
          <p className={styles.heroLabel}>Earn</p>
          <h1 className={styles.heroTitle}>$50 Credits</h1>
          <p className={styles.heroSubtitle}>
            For each friend who successfully protests their property tax with us.
          </p>
          <div className={styles.referralRow}>
            <input
              value={referralShareUrl || referralCode || "Referral link unavailable"}
              readOnly
              className={styles.linkInput}
            />
            <button type="button" className={styles.copyButton} onClick={copyReferralCode}>
              {copied ? "Copied" : "Copy"}
            </button>
            <button type="button" className={styles.iconButton} onClick={shareReferralCode}>
              Share
            </button>
            <button type="button" className={styles.iconButton} onClick={openEmail}>
              Email
            </button>
            <button type="button" className={styles.iconButton} onClick={openSMS}>
              SMS
            </button>
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => setIsQrOpen(true)}
              aria-label="Show referral QR code"
            >
              QR
            </button>
          </div>
        </div>
        <aside className={styles.heroRight}>
          <div className={styles.statsCard}>
            <div>
              <div className={styles.statsValue}>{referralCount}</div>
              <div className={styles.statsLabel}>Total Referrals</div>
            </div>
            <div>
              <div className={styles.statsValue}>{referralStats.completed}</div>
              <div className={styles.statsLabel}>Successful</div>
            </div>
          </div>
          {/* <div className={styles.badgesRow}>
            <span>Starter</span>
            <span>Connector</span>
            <span>Influencer</span>
            <span>Champion</span>
          </div> */}
        </aside>
      </section>

      <section className={styles.summaryBar}>
        <div>
          <p className={styles.summaryLabel}>Total Rewards Summary</p>
          <p className={styles.summaryValue}>${(statistics?.totalPointsUsed ?? 0).toFixed(2)}</p>
        </div>
        {/* <p className={styles.summaryHelp}>Credits are automatically applied at checkout.</p> */}
      </section>

      <section className={styles.activitySection}>
        <div className={styles.tabBar}>
          <button
            type="button"
            className={activeTab === "activity" ? styles.activeTab : styles.tabButton}
            onClick={() => setActiveTab("activity")}
          >
            My Rewards Activity
          </button>
          <button
            type="button"
            className={activeTab === "myRewards" ? styles.activeTab : styles.tabButton}
            onClick={() => setActiveTab("myRewards")}
          >
            My Referrals
          </button>
        </div>

        <h2 className={styles.sectionTitle}>
          {activeTab === "myRewards" ? "My Rewards" : "Rewards Activity"}
        </h2>
        {activeTab === "myRewards" && (
          <div className={styles.filters}>
            {(["all", "pending", "completed"] as const).map((filter) => (
              <button
                key={filter}
                type="button"
                className={statusFilter === filter ? styles.filterActive : styles.filter}
                onClick={() => setStatusFilter(filter)}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>
        )}
        <div
          className={`${styles.tableHeader} ${
            activeTab === "activity" ? styles.tableHeaderActivity : styles.tableHeaderRewards
          }`}
        >
          {activeTab === "myRewards" ? <span>Reward Source</span> : <span>Referral Source</span>}
          {activeTab === "activity" && <span>Referral User</span>}
          {activeTab === "myRewards" ? <span>Date Earned</span> : <span>Date Referred</span>}
          <span>Amount</span>
          <span>Status</span>
        </div>
        <div className={styles.tableBody}>
          {(activeTab === "myRewards" ? isLoadingReferrals : isLoadingTransactions) && (
            <div className={styles.emptyState}>
              {activeTab === "myRewards" ? "Loading rewards..." : "Loading activity..."}
            </div>
          )}
          {!(activeTab === "myRewards" ? isLoadingReferrals : isLoadingTransactions) &&
            activityRows.map((row) => (
              <div
                key={`${row.source}-${row.date}`}
                className={`${styles.tableRow} ${
                  activeTab === "activity" ? styles.tableRowActivity : styles.tableRowRewards
                }`}
              >
                <span>{row.source}</span>
                {activeTab === "activity" && <span>{row.referal_user}</span>}
                <span>{row.date}</span>
                <span
                  className={
                    row.amount.startsWith("+") ? styles.amountPositive : styles.amountNegative
                  }
                >
                  {row?.status?.toLowerCase() === "pending" ? "+$0.00" : row.amount}
                </span>
                <span className={styles.statusText}>{row.status}</span>
              </div>
            ))}
          {!(activeTab === "myRewards" ? isLoadingReferrals : isLoadingTransactions) &&
            activityRows.length === 0 && (
              <div className={styles.emptyState}>
                {activeTab === "myRewards" ? "No rewards found." : "No referral activity found."}
              </div>
            )}
        </div>

        <div className={styles.mobileCards}>
          {(activeTab === "myRewards" ? isLoadingReferrals : isLoadingTransactions) && (
            <div className={styles.emptyState}>
              {activeTab === "myRewards" ? "Loading rewards..." : "Loading activity..."}
            </div>
          )}

          {!(activeTab === "myRewards" ? isLoadingReferrals : isLoadingTransactions) &&
            activityRows.map((row) => (
              <article key={`mobile-${row.source}-${row.date}`} className={styles.mobileCard}>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>
                    {activeTab === "myRewards" ? "Reward Source" : "Referral Source"}
                  </span>
                  <span className={styles.mobileCardValue}>{row.source}</span>
                </div>

                {activeTab === "activity" && (
                  <div className={styles.mobileCardRow}>
                    <span className={styles.mobileCardLabel}>Referral User</span>
                    <span className={styles.mobileCardValue}>{row.referal_user}</span>
                  </div>
                )}

                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>
                    {activeTab === "myRewards" ? "Date Earned" : "Date Referred"}
                  </span>
                  <span className={styles.mobileCardValue}>{row.date}</span>
                </div>

                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Amount</span>
                  <span
                    className={
                      row.amount.startsWith("+")
                        ? `${styles.mobileCardValue} ${styles.amountPositive}`
                        : `${styles.mobileCardValue} ${styles.amountNegative}`
                    }
                  >
                    {row?.status?.toLowerCase() === "pending" ? "+$0.00" : row.amount}
                  </span>
                </div>

                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Status</span>
                  <span className={`${styles.mobileCardValue} ${styles.statusText}`}>
                    {row.status}
                  </span>
                </div>
              </article>
            ))}

          {!(activeTab === "myRewards" ? isLoadingReferrals : isLoadingTransactions) &&
            activityRows.length === 0 && (
              <div className={styles.emptyState}>
                {activeTab === "myRewards" ? "No rewards found." : "No referral activity found."}
              </div>
            )}
        </div>
      </section>

      <section className={styles.howItWorks}>
        <h2 className={styles.howItWorksTitle}>How It Works</h2>
        <p className={styles.howItWorksSubTitle}>Invite Friends, Get Rewarded</p>
        <div className={styles.stepsGrid}>
          <article className={styles.stepCard}>
            <p className={styles.stepNumber}>1</p>
            <h3>Share Your Link</h3>
            <p>Share your unique referral link with friends via social or messengers.</p>
          </article>
          <article className={styles.stepCard}>
            <p className={styles.stepNumber}>2</p>
            <h3>They File a Protest</h3>
            <p>Your friend uses your link to sign up and file their property tax protest.</p>
          </article>
          <article className={styles.stepCard}>
            <p className={styles.stepNumber}>3</p>
            <h3>You Earn Rewards</h3>
            <p>Once their protest is filed, you get rewarded with $50 credits.</p>
          </article>
        </div>
      </section>

      <section className={styles.faqSection}>
        <h2 className={styles.faqTitle}>FAQ&apos;S</h2>
        <div className={styles.faqList}>
          {referralFaqs.map((item, index) => (
            <div key={item.question} className={styles.faqItem}>
              <button
                type="button"
                className={styles.faqQuestion}
                onClick={() => toggleFaq(index)}
                aria-expanded={openFaqIndex === index}
              >
                <span>{item.question}</span>
                <ChevronDownIcon
                  className={`${styles.faqChevron} ${openFaqIndex === index ? styles.faqChevronOpen : ""}`}
                />
              </button>
              {openFaqIndex === index && <p className={styles.faqAnswer}>{item.answer}</p>}
            </div>
          ))}
        </div>
      </section>

      <Modal
        isOpen={isQrOpen}
        onClose={() => setIsQrOpen(false)}
        showCloseButton={false}
        unconstrained
        className={styles.qrModal}
        bodyClassName={styles.qrModalBody}
        ariaLabel="Referral QR code"
      >
        <button
          type="button"
          className={styles.qrClose}
          onClick={() => setIsQrOpen(false)}
          aria-label="Close QR modal"
        >
          ×
        </button>
        <div className={styles.qrTitle}>Scan to invite</div>
        <div className={styles.qrImageWrap}>
          {qrValue ? (
            <QRCode
              size={256}
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              value={qrValue}
              viewBox="0 0 256 256"
            />
          ) : (
            <div className={styles.qrEmpty}>Referral link unavailable.</div>
          )}
        </div>
        <div className={styles.qrLink} title={qrValue}>
          {qrValue || "-"}
        </div>
      </Modal>
    </div>
  );
};
